import React, { useEffect, useRef, useState } from 'react';
import { Logo } from './Logo';

// Types for props
interface StripePrice {
  id?: string;
  priceId?: string;
  unit_amount?: number | null;
  metadata?: { liters?: string };
}

interface StripeProduct {
  id: string;
  name: string;
  description?: string | null;
  metadata?: { liters?: string };
  prices?: StripePrice[];
}

interface LandingPageProps {
  onGetStarted: () => void;
  onLogin: () => void;
  onPurchase: (priceId: string) => void; // Trigger Stripe checkout with priceId
  isLoggedIn: boolean;
  stripeProducts?: any[];
}

// Feature data with Iconify icon names
const FEATURES = [
  {
    icon: 'mdi:robot-outline',
    title: 'AI-Powered Coaching',
    description: 'Real-time feedback from Danny Dime, your Australian welding mentor powered by Gemini 2.5 Flash.',
    gradient: 'from-cyan-500 to-blue-500',
  },
  {
    icon: 'mdi:video-outline',
    title: 'Live Video Analysis',
    description: 'Point your camera at your weld and get instant visual feedback at 2 FPS analysis rate.',
    gradient: 'from-orange-500 to-red-500',
  },
  {
    icon: 'mdi:ruler',
    title: 'Precision Measurements',
    description: 'Calibrate with a reference object and get exact defect measurements in millimeters.',
    gradient: 'from-green-500 to-emerald-500',
  },
  {
    icon: 'mdi:microphone-outline',
    title: 'Voice Conversation',
    description: 'Talk naturally with Danny while welding. Hands-free coaching when you need it most.',
    gradient: 'from-purple-500 to-pink-500',
  },
];

// How it works steps
const STEPS = [
  {
    number: '01',
    title: 'Sign Up Free',
    description: 'Create your account and get 2 minutes of free coaching to try Danny out.',
    icon: 'mdi:account-plus',
  },
  {
    number: '02',
    title: 'Open the Valve',
    description: 'Start a session and grant camera/microphone access.',
    icon: 'mdi:valve',
  },
  {
    number: '03',
    title: 'Start Welding',
    description: 'Danny watches your technique and provides real-time feedback.',
    icon: 'mdi:fire',
  },
  {
    number: '04',
    title: 'Level Up',
    description: 'Track your progress and watch your welds improve over time.',
    icon: 'mdi:trending-up',
  },
];

// Marquee items (tech stack / features)
const MARQUEE_ITEMS = [
  { text: 'Real-time AI', icon: 'mdi:brain' },
  { text: 'Video Analysis', icon: 'mdi:video' },
  { text: 'Voice Coaching', icon: 'mdi:microphone' },
  { text: 'Defect Detection', icon: 'mdi:magnify-scan' },
  { text: '6 Languages', icon: 'mdi:translate' },
  { text: 'Weld Quality Tips', icon: 'mdi:certificate' },
  { text: 'Live Measurements', icon: 'mdi:ruler-square' },
  { text: 'Shop Talk', icon: 'mdi:chat' },
];

// Testimonials
const TESTIMONIALS = [
  {
    quote: "Danny helped me identify undercut issues I'd been missing for years. My TIG welds have never looked better.",
    author: 'Jake M.',
    role: 'Fabrication Shop Owner',
    avatar: 'JM',
  },
  {
    quote: "It's like having a master welder looking over your shoulder 24/7. The Australian accent is a bonus!",
    author: 'Sarah K.',
    role: 'Welding Instructor',
    avatar: 'SK',
  },
  {
    quote: "The measurement calibration feature is incredible. Finally getting real numbers on my defects.",
    author: 'Mike R.',
    role: 'Pipeline Welder',
    avatar: 'MR',
  },
];

// Pricing packages
const PRICING = [
  {
    name: 'Quick Strike',
    liters: 12,
    price: '$1',
    description: '~2 mins of coaching',
    popular: false,
  },
  {
    name: 'Small Cylinder',
    liters: 60,
    price: '$5',
    description: '~10 mins of coaching',
    popular: false,
  },
  {
    name: 'Standard Bottle',
    liters: 300,
    price: '$20',
    description: '~50 mins of coaching',
    popular: true,
  },
  {
    name: 'Industrial Manifold',
    liters: 1000,
    price: '$50',
    description: '~165 mins of coaching',
    popular: false,
  },
];

// Languages (English only for now)
const LANGUAGES = [
  { code: 'en', name: 'English', flag: '🇺🇸' },
];

// Iconify component wrapper
const Icon: React.FC<{ icon: string; className?: string }> = ({ icon, className = '' }) => (
  <span className={`iconify ${className}`} data-icon={icon} data-inline="false"></span>
);

export const LandingPage: React.FC<LandingPageProps> = ({ onGetStarted, onLogin, onPurchase, isLoggedIn, stripeProducts }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState(LANGUAGES[0]);
  const [showLangDropdown, setShowLangDropdown] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const beamRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsVisible(true);

    // Animate beams on mouse move
    const handleMouseMove = (e: MouseEvent) => {
      if (beamRef.current) {
        const rect = beamRef.current.getBoundingClientRect();
        const x = ((e.clientX - rect.left) / rect.width) * 100;
        const y = ((e.clientY - rect.top) / rect.height) * 100;
        beamRef.current.style.setProperty('--mouse-x', `${x}%`);
        beamRef.current.style.setProperty('--mouse-y', `${y}%`);
      }
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-dime-bg text-white overflow-x-hidden">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-dime-bg/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-14 sm:h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <Logo size={32} variant="icon" className="sm:w-10 sm:h-10" />
              <span style={{ fontFamily: 'Bebas Neue, sans-serif' }} className="hidden sm:block text-2xl tracking-wider">DANNY DIME</span>
            </div>
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Features</a>
              <a href="#how-it-works" className="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>How it Works</a>
              <a href="#pricing" className="text-sm text-gray-400 hover:text-white transition-colors uppercase tracking-wider" style={{ fontFamily: 'Oswald, sans-serif' }}>Pricing</a>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              {/* Sign In - Hidden on mobile */}
              {!isLoggedIn && (
                <button
                  onClick={onLogin}
                  className="hidden sm:block px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors"
                >
                  Sign In
                </button>
              )}
              <button
                onClick={onGetStarted}
                className="px-3 sm:px-4 py-1.5 sm:py-2 text-xs sm:text-sm font-medium bg-gradient-to-r from-orange-500 to-red-500 rounded-lg hover:opacity-90 transition-opacity uppercase tracking-wider"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {isLoggedIn ? 'Dashboard' : 'Start Free'}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Beam Effects */}
      <section ref={heroRef} className="relative min-h-screen flex items-center justify-center pt-16 overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-dime-bg">
          {/* Grid Pattern */}
          <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:64px_64px]" />

          {/* Beam Effects Container */}
          <div ref={beamRef} className="absolute inset-0 beam-container">
            {/* Horizontal Beams */}
            <div className="absolute top-1/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent beam-horizontal" />
            <div className="absolute top-3/4 left-0 w-full h-px bg-gradient-to-r from-transparent via-orange-500/50 to-transparent beam-horizontal-reverse" />

            {/* Vertical Beams */}
            <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-cyan-500/30 to-transparent beam-vertical" />
            <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-orange-500/30 to-transparent beam-vertical-reverse" />

            {/* Radial Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-orange-500/10 via-transparent to-transparent rounded-full blur-3xl" />

            {/* Spotlight Effect */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_var(--mouse-x,50%)_var(--mouse-y,50%),rgba(34,211,238,0.08)_0%,transparent_50%)]" />
          </div>

          {/* Floating Particles */}
          <div className="particles-container">
            {[...Array(20)].map((_, i) => (
              <div
                key={i}
                className="particle"
                style={{
                  left: `${Math.random() * 100}%`,
                  top: `${Math.random() * 100}%`,
                  animationDelay: `${Math.random() * 5}s`,
                  animationDuration: `${3 + Math.random() * 4}s`,
                }}
              />
            ))}
          </div>
        </div>

        {/* Hero Content */}
        <div className={`relative z-10 max-w-5xl mx-auto px-4 text-center transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-8 backdrop-blur-sm">
            <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span className="text-sm text-gray-300">Powered by Gemini 2.5 Flash</span>
          </div>

          {/* Main Headline */}
          <h1 className="mb-6">
            <span className="block text-white text-2xl sm:text-3xl uppercase tracking-[0.3em] mb-2" style={{ fontFamily: 'Oswald, sans-serif' }}>Your AI</span>
            <span className="block text-6xl sm:text-7xl lg:text-8xl bg-gradient-to-r from-orange-400 via-red-500 to-orange-400 bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              WELDING COACH
            </span>
          </h1>

          {/* Subtitle */}
          <p className="text-lg sm:text-xl text-gray-400 max-w-2xl mx-auto mb-6 leading-relaxed">
            Meet Danny Dime — a rugged Australian welder who watches your technique in real-time and gives you straight-up feedback to level up your welds.
          </p>

          {/* Free Trial Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-orange-500/10 border border-orange-500/20 mb-10">
            <Icon icon="mdi:gift-outline" className="text-orange-500" />
            <span className="text-sm text-orange-400 font-medium">2 minutes free coaching — no credit card required</span>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
            <button
              onClick={onGetStarted}
              className="group relative px-8 py-4 text-lg font-semibold rounded-xl overflow-hidden transition-all duration-300 hover:scale-105"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500" />
              <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="relative flex items-center gap-2">
                <Icon icon="mdi:fire" className="text-xl" />
                Open the Valve
              </span>
            </button>
            <a
              href="#how-it-works"
              className="px-8 py-4 text-lg font-medium text-gray-300 rounded-xl border border-white/10 hover:bg-white/5 hover:border-white/20 transition-all flex items-center gap-2"
            >
              <Icon icon="mdi:play-circle-outline" className="text-xl" />
              See How It Works
            </a>
          </div>

          {/* Hero Visual */}
          <div className="relative mx-auto max-w-3xl w-full">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-orange-500/10">
              {/* Video takes full width - the hero shot */}
              <div className="relative aspect-video w-full overflow-hidden">
                <video
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-full object-cover"
                  src="/coaching-demo.mp4"
                />
                {/* Overlay HUD elements on top of video */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/40 pointer-events-none" />
                
                {/* Top bar overlay */}
                <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-3 sm:px-4 py-2 sm:py-3">
                  <div className="flex items-center gap-1.5 sm:gap-2">
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-red-500" />
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-yellow-500" />
                    <div className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-green-500" />
                  </div>
                  <div className="px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm text-[10px] sm:text-xs text-gray-300 border border-white/10">
                    Live Coaching Session
                  </div>
                  <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/50 backdrop-blur-sm border border-white/10">
                    <Icon icon="mdi:gas-cylinder" className="text-orange-500 text-xs" />
                    <span className="text-[10px] sm:text-xs text-orange-400 font-bold">156L</span>
                  </div>
                </div>

                {/* Danny's chat bubble overlay - bottom right */}
                <div className="absolute bottom-3 right-3 left-3 sm:left-auto sm:max-w-[240px]">
                  <div className="flex items-start gap-2 bg-black/60 backdrop-blur-md rounded-xl p-3 border border-white/10">
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center flex-shrink-0">
                      <Icon icon="mdi:robot" className="text-sm text-white" />
                    </div>
                    <div>
                      <div className="text-[9px] font-bold text-orange-400 uppercase tracking-wider mb-0.5">Danny</div>
                      <div className="text-xs text-gray-200 leading-relaxed">
                        "G'day mate! Running a bit hot there. Ease off the pedal and tighten up that arc length..."
                      </div>
                    </div>
                  </div>
                </div>

                {/* Defect detection marker overlay - top left area */}
                <div className="absolute top-12 sm:top-14 left-3 sm:left-4">
                  <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-red-500/20 backdrop-blur-sm border border-red-500/40">
                    <div className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse" />
                    <span className="text-[9px] sm:text-[10px] font-bold text-red-400 uppercase tracking-wider">Porosity Detected</span>
                  </div>
                </div>
              </div>

              {/* Glow Effect */}
              <div className="absolute -inset-px rounded-2xl bg-gradient-to-r from-orange-500/20 via-transparent to-cyan-500/20 -z-10 blur-sm" />
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 text-gray-500">
          <span className="text-sm">Scroll to explore</span>
          <div className="w-6 h-10 rounded-full border border-gray-700 flex items-start justify-center p-2">
            <div className="w-1.5 h-1.5 rounded-full bg-gray-500 animate-bounce" />
          </div>
        </div>
      </section>

      {/* Marquee Section */}
      <section className="py-12 border-y border-white/5 bg-dime-card/50 overflow-hidden">
        <div className="marquee-container">
          <div className="marquee-content">
            {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, index) => (
              <div key={index} className="flex items-center gap-3 mx-8">
                <Icon icon={item.icon} className="text-xl text-cyan-500" />
                <span className="text-gray-400 whitespace-nowrap">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-cyan-500 tracking-[0.3em] uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>Features</span>
            <h2 className="text-4xl sm:text-5xl mt-4 mb-4 tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              EVERYTHING YOU NEED TO <span className="text-gradient">LEVEL UP</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Professional-grade welding feedback powered by cutting-edge AI technology.
            </p>
          </div>

          {/* Feature Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {FEATURES.map((feature, index) => (
              <div
                key={index}
                className="group relative p-6 rounded-2xl bg-dime-card border border-white/5 hover:border-white/10 transition-all duration-300 hover:-translate-y-1"
              >
                {/* Icon */}
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} p-0.5 mb-4`}>
                  <div className="w-full h-full rounded-xl bg-dime-card flex items-center justify-center">
                    <Icon icon={feature.icon} className={`text-2xl bg-gradient-to-br ${feature.gradient} bg-clip-text text-transparent`} />
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-lg mb-2 uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>

                {/* Hover Glow */}
                <div className={`absolute inset-0 rounded-2xl bg-gradient-to-br ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity -z-10`} />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-24 bg-dime-card/30 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-orange-500 tracking-[0.3em] uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>How It Works</span>
            <h2 className="text-4xl sm:text-5xl mt-4 mb-4 tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              GET STARTED IN <span className="text-gradient">MINUTES</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              From signup to your first coaching session in four simple steps.
            </p>
          </div>

          {/* Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((step, index) => (
              <div key={index} className="relative">
                {/* Connector Line */}
                {index < STEPS.length - 1 && (
                  <div className="hidden lg:block absolute top-8 left-[calc(50%+2rem)] w-[calc(100%-4rem)] h-px bg-gradient-to-r from-white/20 to-transparent" />
                )}

                <div className="text-center">
                  {/* Number Badge */}
                  <div className="relative inline-flex items-center justify-center w-16 h-16 mb-6">
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-orange-500/20 to-red-500/20 animate-pulse" />
                    <div className="relative w-14 h-14 rounded-full bg-dime-card border border-white/10 flex items-center justify-center">
                      <Icon icon={step.icon} className="text-2xl text-orange-500" />
                    </div>
                    <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-gradient-to-br from-orange-500 to-red-500 text-xs font-bold flex items-center justify-center">
                      {step.number.slice(-1)}
                    </span>
                  </div>

                  {/* Content */}
                  <h3 className="text-lg mb-2 uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>{step.title}</h3>
                  <p className="text-sm text-gray-400">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-cyan-500 tracking-[0.3em] uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>Pricing</span>
            <h2 className="text-4xl sm:text-5xl mt-4 mb-4 tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              PAY AS YOU <span className="text-gradient">WELD</span>
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              Start with 2 minutes free, then top up your tank. No subscriptions — just pay as you weld. 1L = 10 seconds of coaching.
            </p>
          </div>

          {/* Pricing Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(() => {
              // Use Stripe products if available, otherwise fallback to static PRICING
              const plans = (stripeProducts && stripeProducts.length > 0)
                ? stripeProducts.map((product) => {
                    const firstPrice = Array.isArray(product.prices) ? product.prices[0] : null;
                    console.log('[LandingPage] Product:', product.name, 'firstPrice:', JSON.stringify(firstPrice));
                    const liters = firstPrice?.metadata?.liters || product.metadata?.liters || '';
                    const priceId = firstPrice?.id || (firstPrice as any)?.priceId || (firstPrice as any)?.price || '';
                    console.log('[LandingPage] Resolved priceId:', priceId);
                    const displayPrice = firstPrice?.unit_amount ? `$${(firstPrice.unit_amount / 100).toFixed(0)}` : '$?';
                    const isPopular = product.name?.toLowerCase().includes('standard') || (liters === '300');
                    return { name: product.name, description: product.description || '', price: displayPrice, liters: liters + 'L', popular: isPopular, priceId };
                  })
                : PRICING.map((p) => ({ ...p, liters: p.liters + 'L', priceId: '' }));

              return plans.map((plan, index) => (
              <div
                key={index}
                className={`relative p-6 rounded-2xl ${
                  plan.popular
                    ? 'bg-gradient-to-b from-orange-500/10 to-transparent border-orange-500/30'
                    : 'bg-dime-card border-white/5'
                } border transition-all duration-300 hover:-translate-y-1`}
              >
                {plan.popular && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-orange-500 to-red-500 text-xs font-semibold">
                    Most Popular
                  </div>
                )}

                <div className="text-center">
                  <Icon icon="mdi:gas-cylinder" className={`text-3xl mb-4 ${plan.popular ? 'text-orange-500' : 'text-gray-500'}`} />
                  <h3 className="text-lg mb-1 uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif' }}>{plan.name}</h3>
                  <p className="text-sm text-gray-500 mb-4">{plan.description}</p>

                  <div className="mb-6">
                    <span className="text-4xl font-bold">{plan.price}</span>
                  </div>

                  <div className="flex items-center justify-center gap-2 text-sm text-gray-400 mb-6">
                    <Icon icon="mdi:fire" className="text-orange-500" />
                    <span>{plan.liters} of Argon</span>
                  </div>

                  <button
                    onClick={() => plan.priceId ? onPurchase(plan.priceId) : onPurchase('')}
                    className={`w-full py-3 rounded-lg font-medium transition-all ${
                      plan.popular
                        ? 'bg-gradient-to-r from-orange-500 to-red-500 hover:opacity-90'
                        : 'bg-white/5 border border-white/10 hover:bg-white/10'
                    }`}
                  >
                    Select Plan
                  </button>
                </div>
              </div>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-dime-card/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-16">
            <span className="text-sm font-medium text-orange-500 tracking-[0.3em] uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>Testimonials</span>
            <h2 className="text-4xl sm:text-5xl mt-4 mb-4 tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              TRUSTED BY <span className="text-gradient">WELDERS</span> WORLDWIDE
            </h2>
          </div>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((testimonial, index) => (
              <div
                key={index}
                className="p-6 rounded-2xl bg-dime-card border border-white/5 hover:border-white/10 transition-all"
              >
                {/* Quote */}
                <Icon icon="mdi:format-quote-open" className="text-3xl text-orange-500/30 mb-4" />
                <p className="text-gray-300 mb-6 leading-relaxed">"{testimonial.quote}"</p>

                {/* Author */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center text-sm font-semibold">
                    {testimonial.avatar}
                  </div>
                  <div>
                    <p className="font-medium">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-24 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-orange-500/5 to-transparent" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-orange-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="text-4xl sm:text-6xl mb-6 tracking-wide" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
            READY TO START <span className="text-gradient">STACKING DIMES</span>?
          </h2>
          <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
            Join thousands of welders improving their technique with Danny Dime's real-time AI coaching.
          </p>
          <button
            onClick={onGetStarted}
            className="group relative px-10 py-5 text-xl rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 uppercase tracking-wider"
            style={{ fontFamily: 'Oswald, sans-serif' }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-red-500" />
            <div className="absolute inset-0 bg-gradient-to-r from-orange-600 to-red-600 opacity-0 group-hover:opacity-100 transition-opacity" />
            <span className="relative flex items-center gap-3">
              <Icon icon="mdi:fire" className="text-2xl" />
              Start Your Free 2-Minute Trial
            </span>
          </button>
          <p className="text-sm text-gray-500 mt-4">No credit card required • 2 minutes free coaching</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <Logo size={40} variant="icon" />
              <span className="text-2xl tracking-wider" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>DANNY DIME</span>
            </div>

            {/* Links */}
            <div className="flex items-center gap-8 text-sm text-gray-400">
              <a href="#features" className="hover:text-white transition-colors">Features</a>
              <a href="#pricing" className="hover:text-white transition-colors">Pricing</a>
              <a href="https://dimevision.app" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">DimeVision</a>
            </div>
          </div>

          {/* Copyright */}
          <div className="mt-8 pt-8 border-t border-white/5 text-center text-sm text-gray-500">
            <p>© {new Date().getFullYear()} Danny Dime. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
